'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { GREAT_WHITE, GYPSUM, OLAF, OZ } from 'HubStyleTokens/colors';
import H2 from 'UIComponents/elements/headings/H2';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIModal from 'UIComponents/dialog/UIModal';
import UITable from 'UIComponents/table/UITable';
import UIImage from 'UIComponents/image/UIImage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import { getFeatureCompatibility } from '../../../selectors/details';
import { getFeatureCompatibilityRequestState } from '../../../selectors/requestStates';
import { getPricingUrl } from '../../../utils/urls';
import { flattenedTierOrder } from '../../../utils/compatibility';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import IntegrationFeature from 'ecosystem-marketplace-core/records/IntegrationFeature';
import * as Tier from 'ecosystem-marketplace-core/constants/groupTags/Tier';
import * as TierGroups from 'ecosystem-marketplace-core/constants/groupTags/TierGroup';
import { getTierI18nKey, getTierHubI18nKey, getTierLevelI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { ProductLinePageIcons, CRM } from 'ui-addon-upgrades/_core/productPage/ProductPageIcons';
import { getIsCustomer } from '../../../selectors/auth';
const borderStyles = css(["border-left:", ";"], props => props.showBorder ? `1px solid ${GREAT_WHITE} !important` : 0);
const TR = styled.tr.withConfig({
  displayName: "FeatureCompatibilityModal__TR",
  componentId: "sc-190glhp-0"
})(["background-color:", " !important;&:hover{background-color:", " !important;}"], props => props.index % 2 === 0 ? GYPSUM : OLAF, props => props.index % 2 === 0 ? GYPSUM : OLAF);
const TH = styled.th.withConfig({
  displayName: "FeatureCompatibilityModal__TH",
  componentId: "sc-190glhp-1"
})(["", ";text-transform:uppercase;font-weight:600;font-size:12px;padding:12px;"], borderStyles);
const TD = styled.td.withConfig({
  displayName: "FeatureCompatibilityModal__TD",
  componentId: "sc-190glhp-2"
})(["", ";height:", ";"], borderStyles, props => props.compact ? '48px !important' : 'inherit');

const ScrollContainer = (_ref) => {
  let {
    scrollDirection
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["scrollDirection"]);

  return /*#__PURE__*/_jsx(UIScrollContainer, Object.assign({}, rest, {
    size: 40,
    style: {
      height: scrollDirection === 'both' ? '100%' : 'initial'
    }
  }));
};

const textPath = 'appMarketplace.detail.overview.featureCompatibility';

const FeatureCompatibilityModal = ({
  integrationFeatures,
  onClose,
  trackInteraction
}) => {
  const isCustomer = useSelector(getIsCustomer);
  const {
    compatibility,
    portalTiers
  } = useSelector(getFeatureCompatibility);
  const compatibilityStatus = useSelector(getFeatureCompatibilityRequestState);
  const getIsFeatureTierCompatible = useCallback((integrationFeature, tier) => integrationFeature.get('hubSpotFeatures').every(hubSpotFeature => {
    const requiredTiers = compatibility.get(hubSpotFeature);
    return !requiredTiers || !requiredTiers.size || requiredTiers.includes(tier);
  }), [compatibility]);
  const renderedHubsHeader = useMemo(() => /*#__PURE__*/_jsxs(TR, {
    index: 0,
    style: {
      whiteSpace: 'nowrap',
      fontWeight: 600
    },
    children: [/*#__PURE__*/_jsx(TD, {}), Object.keys(TierGroups).map(tierGroup => /*#__PURE__*/_jsx(TD, {
      colSpan: TierGroups[tierGroup].length,
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "center",
        children: [/*#__PURE__*/_jsx(UIImage, {
          className: "display-inline-block m-right-2",
          src: ProductLinePageIcons[tierGroup] || CRM,
          width: 16
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: getTierHubI18nKey(tierGroup)
        })]
      })
    }, tierGroup))]
  }), []);
  const renderedHubLevels = useMemo(() => /*#__PURE__*/_jsxs(TR, {
    index: 1,
    compact: true,
    style: {
      whiteSpace: 'nowrap',
      height: 48
    },
    children: [/*#__PURE__*/_jsx(TH, {
      style: {
        textAlign: 'start',
        padding: '12px 24px'
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.integrationFeatures`
      })
    }), Object.keys(TierGroups).map(tierGroup => TierGroups[tierGroup].map((tier, index) => {
      const tierLevel = tier.split('_')[1];
      const isUserPlan = portalTiers.size ? portalTiers.includes(tier) : tier === Tier.HUBSPOT_FREE;
      return /*#__PURE__*/_jsxs(TH, {
        showBorder: index === 0,
        "aria-label": I18n.text(getTierI18nKey(tier)),
        children: [isUserPlan && isCustomer ? /*#__PURE__*/_jsx("div", {
          style: {
            marginTop: -30,
            marginBottom: 10
          },
          children: /*#__PURE__*/_jsx(UIBadge, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${textPath}.yourPlan`
            })
          })
        }) : null, /*#__PURE__*/_jsx(FormattedMessage, {
          message: getTierLevelI18nKey(tierLevel)
        })]
      }, tier);
    }))]
  }), [portalTiers, isCustomer]);
  const renderedFeatureRows = useMemo(() => integrationFeatures.map((integrationFeature, featureIndex) => /*#__PURE__*/_jsxs(TR, {
    index: featureIndex,
    children: [/*#__PURE__*/_jsx(TD, {
      style: {
        textAlign: 'start'
      },
      children: integrationFeature.get('headline')
    }), Object.keys(TierGroups).map(tierGroup => TierGroups[tierGroup].map((tier, index) => {
      const isFeatureCompatible = getIsFeatureTierCompatible(integrationFeature, tier);
      return /*#__PURE__*/_jsx(TD, {
        showBorder: index === 0,
        children: isFeatureCompatible ? /*#__PURE__*/_jsx(UIIcon, {
          name: "success",
          color: OZ
        }) : null
      }, `${tier}-feature-${featureIndex}`);
    }))]
  }, `integration-feature-row-${featureIndex}`)), [getIsFeatureTierCompatible, integrationFeatures]);
  const renderedFooter = useMemo(() => /*#__PURE__*/_jsxs(TR, {
    index: integrationFeatures.size,
    children: [/*#__PURE__*/_jsx(TD, {
      compact: true
    }), Object.keys(TierGroups).map(tierGroup => /*#__PURE__*/_jsx(TD, {
      colSpan: TierGroups[tierGroup].length,
      compact: true,
      showBorder: true,
      style: {
        fontSize: 12,
        whiteSpace: 'nowrap'
      },
      children: /*#__PURE__*/_jsx(UILink, {
        external: true,
        href: getPricingUrl(isCustomer, tierGroup),
        onClick: () => trackInteraction('click compatibility modal pricing link', 'overview', tierGroup),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.${tierGroup === 'CRM' ? 'seeFeatures' : 'seePricingAndFeatures'}`
        })
      })
    }, tierGroup))]
  }), [isCustomer, integrationFeatures, trackInteraction]);
  return /*#__PURE__*/_jsxs(UIModal, {
    width: "90%",
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.header`
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      className: "p-all-0",
      children: /*#__PURE__*/_jsx(LoadingWrapper, {
        requestStatus: compatibilityStatus,
        children: /*#__PURE__*/_jsxs(UITable, {
          ScrollContainer: ScrollContainer,
          style: {
            textAlign: 'center'
          },
          children: [/*#__PURE__*/_jsxs("colgroup", {
            children: [/*#__PURE__*/_jsx("col", {
              style: {
                minWidth: 350
              }
            }), /*#__PURE__*/_jsx("col", {
              span: flattenedTierOrder.length
            })]
          }), /*#__PURE__*/_jsxs("tbody", {
            children: [renderedHubsHeader, renderedHubLevels, renderedFeatureRows, renderedFooter]
          })]
        })
      })
    })]
  });
};

FeatureCompatibilityModal.propTypes = {
  integrationFeatures: ImmutablePropTypes.listOf(PropTypes.instanceOf(IntegrationFeature)).isRequired,
  onClose: PropTypes.func.isRequired,
  trackInteraction: PropTypes.func.isRequired
};
export default FeatureCompatibilityModal;